import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import NotificationToast from '../../components/ui/NotificationToast';
import AttendancePeriodSelector from './components/AttendancePeriodSelector';
import AttendanceStatsBanner from './components/AttendanceStatsBanner';
import AttendanceActionBar from './components/AttendanceActionBar';
import AttendanceTable from './components/AttendanceTable';
import EmployeeAttendanceDetailModal from './components/EmployeeAttendanceDetailModal';
import PunchAttendanceModal from './components/PunchAttendanceModal';
import Button from '../../components/ui/Button';
import attendanceService from '../../services/attendanceService';
import departmentsService from '../../services/departmentsService';

const AttendanceManagement = () => {
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [employees, setEmployees] = useState([]);
  const [overview, setOverview] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);

  // Detail & Punch Modal state
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedEmployeeForLog, setSelectedEmployeeForLog] = useState(null);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [selectedEmployeeForPunch, setSelectedEmployeeForPunch] = useState(null);

  // Mock user data for header
  const user = {
    name: "HR Administrator",
    email: "hr@facilitypro.com",
    role: "HR Manager"
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    fetchAttendanceSummary();
  }, [selectedPeriod, filterDepartment]);

  const fetchDepartments = async () => {
    try {
      if (departmentsService && typeof departmentsService.getDepartments === 'function') {
        const res = await departmentsService.getDepartments();
        if (res.success && res.data) {
          setDepartments(res.data);
        }
      }
    } catch (e) {
      console.warn('[Attendance Management] Departments service fallback:', e);
    }
  };

  const fetchAttendanceSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const [year, month] = selectedPeriod.split('-');
      const params = {
        month: `${year}-${month}`
      };
      if (filterDepartment) {
        params.department = filterDepartment;
      }

      const res = await attendanceService.getMonthlyAttendanceSummary(params);
      if (res.success && res.data) {
        setEmployees(res.data.employees || []);
        setOverview(res.data.overview || null);
      } else {
        setEmployees([]);
        setOverview(null);
        if (res.error) setError(res.error);
      }
    } catch (err) {
      console.error('[Attendance Management] Error fetching summary:', err);
      setError('Failed to load attendance summary records.');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 4)]);
  };

  const handleNotificationDismiss = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    addNotification({
      id: Date.now(),
      type: 'info',
      title: 'Period Updated',
      message: `Attendance period changed to ${period}`,
      timestamp: new Date()
    });
  };

  const handleViewEmployeeLog = (emp) => {
    setSelectedEmployeeForLog(emp);
    setShowLogModal(true);
  };

  const handleMarkAttendance = (emp) => {
    setSelectedEmployeeForPunch(emp);
    setShowPunchModal(true);
  };

  const handlePunchSaved = (record) => {
    fetchAttendanceSummary();
    addNotification({
      id: Date.now(),
      type: 'success',
      title: 'Punch Recorded',
      message: `Successfully saved punch record for ${selectedEmployeeForPunch?.full_name || 'employee'}`,
      timestamp: new Date()
    });
  };

  const handleExportCSV = () => {
    try {
      if (!employees || employees.length === 0) {
        addNotification({
          id: Date.now(),
          type: 'warning',
          title: 'Export Warning',
          message: 'No employee records to export',
          timestamp: new Date()
        });
        return;
      }

      const headers = ['Employee Code', 'Full Name', 'Department', 'Position', 'Days Present', 'Days Late', 'Days Absent', 'Half Days', 'Work Hours', 'Overtime Hours', 'Attendance Rate (%)'];
      const rows = filteredEmployees.map(emp => [
        `"${emp.employee_code || ''}"`,
        `"${emp.full_name || ''}"`,
        `"${emp.department || ''}"`,
        `"${emp.position || ''}"`,
        emp.days_present,
        emp.days_late,
        emp.days_absent,
        emp.half_days,
        emp.work_hours,
        emp.overtime_hours,
        `"${emp.attendance_rate}%"`
      ]);

      const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `attendance_report_${selectedPeriod}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification({
        id: Date.now(),
        type: 'success',
        title: 'Export Complete',
        message: `Exported attendance report for ${filteredEmployees.length} employees`,
        timestamp: new Date()
      });
    } catch (e) {
      addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Export Failed',
        message: 'Could not generate CSV export',
        timestamp: new Date()
      });
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      (emp.full_name || '').toLowerCase().includes(search) ||
      (emp.employee_code || '').toLowerCase().includes(search) ||
      (emp.position || '').toLowerCase().includes(search);

    const matchesDept = !filterDepartment || (emp.department || '').toLowerCase() === filterDepartment.toLowerCase();

    let matchesStatus = true;
    if (filterStatus === 'high') matchesStatus = emp.attendance_rate >= 90;
    else if (filterStatus === 'medium') matchesStatus = emp.attendance_rate >= 75 && emp.attendance_rate < 90;
    else if (filterStatus === 'low') matchesStatus = emp.attendance_rate < 75;
    else if (filterStatus === 'late_risks') matchesStatus = emp.days_late > 2;

    return matchesSearch && matchesDept && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        notifications={notifications}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6 min-w-0 max-w-full overflow-hidden">
          <BreadcrumbNavigation />

          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Attendance Management</h1>
              <p className="text-muted-foreground">
                Track, monitor, and aggregate monthly workforce attendance and tardiness stats
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => window.location.href = '/erp/selfie-attendance'}
                iconName="Camera"
                iconPosition="left"
              >
                Selfie Punch Portal
              </Button>
            </div>
          </div>

          {/* Period Selector */}
          <AttendancePeriodSelector
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
            totalEmployees={overview?.total_employees || employees.length}
          />

          {/* Overview Stats Cards */}
          <AttendanceStatsBanner overview={overview} />

          {/* Action Bar (Search & Filters) */}
          <AttendanceActionBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterDepartment={filterDepartment}
            onDepartmentChange={setFilterDepartment}
            filterStatus={filterStatus}
            onStatusChange={setFilterStatus}
            onExportCSV={handleExportCSV}
            departments={departments}
          />

          {/* Main Table / Grid */}
          {loading ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center shadow-sm">
              <p className="text-muted-foreground">Loading workforce attendance data...</p>
            </div>
          ) : error ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center shadow-sm">
              <p className="text-rose-500 mb-4">{error}</p>
              <Button onClick={fetchAttendanceSummary} variant="outline">
                Retry Loading
              </Button>
            </div>
          ) : (
            <AttendanceTable
              employees={filteredEmployees}
              onViewEmployeeLog={handleViewEmployeeLog}
              onMarkAttendance={handleMarkAttendance}
              selectedPeriod={selectedPeriod}
            />
          )}
        </div>
      </main>

      {/* Detailed Employee Log Modal */}
      <EmployeeAttendanceDetailModal
        isOpen={showLogModal}
        onClose={() => {
          setShowLogModal(false);
          setSelectedEmployeeForLog(null);
        }}
        employee={selectedEmployeeForLog}
        selectedPeriod={selectedPeriod}
      />

      {/* Mark Punch Attendance Modal */}
      <PunchAttendanceModal
        isOpen={showPunchModal}
        onClose={() => {
          setShowPunchModal(false);
          setSelectedEmployeeForPunch(null);
        }}
        employee={selectedEmployeeForPunch}
        onSaved={handlePunchSaved}
      />

      <NotificationToast
        notifications={notifications}
        onDismiss={handleNotificationDismiss}
        position="top-right"
      />
    </div>
  );
};

export default AttendanceManagement;
